import { domainCodeAtom, useSaladBar } from '@australiangreens/ag-internal-components';
import { SelectChangeEvent } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ChangeEvent, SyntheticEvent, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePrecacheUserContactPerms } from '@/hooks';
import { Search, SearchDbRecordResult, SearchTemplate } from '@/types';
import { useLookupUsers } from '@components/UsersFetchAutocomplete';
import {
  useDeleteSearch,
  useDeleteSearchTemplate,
  useSearches,
  useSearchTemplates,
} from './hooks';
import { buildSearchLibraryTemplateShareUrl } from './searchLibraryTemplateUrl';
import {
  Auth0Snapshot,
  searchColumnVisibilityAtom,
  searchCreatedByAtom,
  searchDescriptionAtom,
  searchNameAtom,
  searchPageNoAtom,
  searchPageSizeAtom,
  searchSortAtom,
  searchTabSelectionAtom,
  searchTemplateColumnVisibilityAtom,
  searchTemplateCreatedByAtom,
  searchTemplateDescriptionAtom,
  searchTemplateForCopyAtom,
  searchTemplateNameAtom,
  searchTemplatePageNoAtom,
  searchTemplatePageSizeAtom,
  searchTemplateSortAtom,
  searchTypeAtom,
} from './atoms';
import { useSavedSearchesTemplateLoad } from './useSavedSearchesTemplateLoad';

export function useSavedSearchesPage() {
  const domainCode = useAtomValue(domainCodeAtom);
  const setSearchTemplateForCopyAtom = useSetAtom(searchTemplateForCopyAtom);
  const lookupUsers = useLookupUsers();
  usePrecacheUserContactPerms();
  const navigate = useNavigate();
  const { enqueueSuccessNotification } = useSaladBar();
  const [searchForDelete, setSearchForDelete] = useState<Search | undefined>();
  const [searchTemplateForDelete, setSearchTemplateForDelete] = useState<
    SearchTemplate | undefined
  >();
  const [searchTemplateIdEdit, setSearchTemplateIdEdit] = useState<number | undefined>();
  const [searchTemplateNameEdit, setSearchTemplateNameEdit] = useState<string>('');
  const [searchTemplateDescriptionEdit, setSearchTemplateDescriptionEdit] = useState<string>('');
  const [originalSearchTemplateName, setOriginalSearchTemplateName] = useState<string>('');

  const [searchForEdit, setSearchForEdit] = useState<Search | undefined>();
  const [searchType, setSearchType] = useAtom(searchTypeAtom);
  const [searchName, setSearchName] = useAtom(searchNameAtom);
  const [searchCreatedBy, setSearchCreatedBy] = useAtom(searchCreatedByAtom);
  const [searchDescription, setSearchDescription] = useAtom(searchDescriptionAtom);

  const [searchColumnVisibilityAtomModel, setSearchColumnVisibilityAtomModel] = useAtom(
    searchColumnVisibilityAtom
  );
  const [searchSortAtomModel, setSearchSortAtomModel] = useAtom(searchSortAtom);
  const [searchPageSizeAtomModel, setSearchPageSizeAtomModel] = useAtom(searchPageSizeAtom);
  const [searchPageNoAtomModel, setSearchPageNoAtomModel] = useAtom(searchPageNoAtom);

  const [searchTemplateColumnVisibilityAtomModel, setSearchTemplateColumnVisibilityAtomModel] =
    useAtom(searchTemplateColumnVisibilityAtom);
  const [searchTemplateSortAtomModel, setSearchTemplateSortAtomModel] =
    useAtom(searchTemplateSortAtom);
  const [searchTemplatePageSizeAtomModel, setSearchTemplatePageSizeAtomModel] = useAtom(
    searchTemplatePageSizeAtom
  );
  const [searchTemplatePageNoAtomModel, setSearchTemplatePageNoAtomModel] =
    useAtom(searchTemplatePageNoAtom);

  const [searchTemplateName, setSearchTemplateName] = useAtom(searchTemplateNameAtom);
  const [searchTemplateCreatedBy, setSearchTemplateCreatedBy] = useAtom(
    searchTemplateCreatedByAtom
  );
  const [searchTemplateDescription, setSearchTemplateDescription] = useAtom(
    searchTemplateDescriptionAtom
  );

  const { data: searches, isInitialLoading: isInitialSearchLoading } = useSearches();
  const { mutate: mutateDeleteSearch } = useDeleteSearch();
  const { mutate: mutateDeleteSearchTemplate } = useDeleteSearchTemplate();

  const { data: searchTemplates, isInitialLoading: isInitialSearchTemplateLoading } =
    useSearchTemplates(domainCode);

  const [searchesTabSelection, setSearchesTabSelection] = useAtom(searchTabSelectionAtom);

  const {
    searchTemplateForLoad,
    templateLoadError,
    handleCloseLoadTemplateModal,
    handleCloseTemplateLoadError,
  } = useSavedSearchesTemplateLoad();

  const activateCopyLink = (searchTemplate: SearchTemplate) => {
    const url = buildSearchLibraryTemplateShareUrl(searchTemplate.id);
    void navigator.clipboard.writeText(url).then(() => {
      enqueueSuccessNotification('Link copied');
    });
  };

  function searchRowsFilter(search: SearchDbRecordResult) {
    const searchTypeFilter = searchType ? searchType === search.searchType : true;
    const searchNameFilter =
      searchName.length >= 3 ? search.name?.toLowerCase().includes(searchName.toLowerCase()) : true;

    const searchDescriptionFilter =
      searchDescription.length >= 3
        ? search.description?.toLowerCase().includes(searchDescription.toLowerCase())
        : true;

    const createdByFilter = searchCreatedBy ? searchCreatedBy.label === search.savedBySub : true;

    return searchTypeFilter && searchNameFilter && searchDescriptionFilter && createdByFilter;
  }

  function searchTemplatesRowsFilter(searchTemplate: SearchTemplate) {
    const searchTemplateNameFilter =
      searchTemplateName.length >= 3
        ? searchTemplate.name?.toLowerCase().includes(searchTemplateName.toLowerCase())
        : true;

    const searchTemplateDescriptionFilter =
      searchTemplateDescription.length >= 3
        ? searchTemplate.description
            ?.toLowerCase()
            .includes(searchTemplateDescription.toLowerCase())
        : true;

    const createdByFilter = searchTemplateCreatedBy
      ? searchTemplateCreatedBy.label === searchTemplate.createdBySub
      : true;

    return searchTemplateNameFilter && searchTemplateDescriptionFilter && createdByFilter;
  }

  const mySearchesMapped = searches
    ?.map((search) => ({ ...search, Ellipsis: search.id }))
    .filter(searchRowsFilter);

  const mySearchTemplatesMapped = searchTemplates
    ?.map((searchTemplate) => ({
      ...searchTemplate,
      Ellipsis: searchTemplate.id,
    }))
    .filter(searchTemplatesRowsFilter);

  const handleDeleteSearchDialogDelete = async () => {
    if (searchForDelete) mutateDeleteSearch({ searchId: searchForDelete?.id });
    setSearchForDelete(undefined);
  };

  const handleDeleteSearchTemplateDialogDelete = async () => {
    if (searchTemplateForDelete)
      mutateDeleteSearchTemplate({ searchTemplateId: searchTemplateForDelete?.id });
    setSearchTemplateForDelete(undefined);
  };

  const handleSearchNameChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchName(event.target.value);
  };

  const handleSearchDescriptionChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchDescription(event.target.value);
  };

  const handleSearchCreatedByChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: Auth0Snapshot | null
  ) => {
    setSearchCreatedBy(newValue);
  };

  const handleSearchTypeChange = (event: SelectChangeEvent) => {
    setSearchType(event.target.value as string);
  };

  const handleResetFilter = () => {
    setSearchType('');
    setSearchName('');
    setSearchDescription('');
    setSearchCreatedBy(null);
  };

  const handleSearchTemplateNameChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTemplateName(event.target.value);
  };

  const handleSearchTemplateDescriptionChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTemplateDescription(event.target.value);
  };

  const handleSearchTemplateCreatedByChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: Auth0Snapshot | null
  ) => {
    setSearchTemplateCreatedBy(newValue);
  };

  const handleResetSearchTemplateFilter = () => {
    setSearchTemplateName('');
    setSearchTemplateDescription('');
    setSearchTemplateCreatedBy(null);
  };

  const setParametersForTemplateEdit = (searchTemplate: SearchTemplate) => {
    setSearchTemplateIdEdit(searchTemplate.id);
    setSearchTemplateNameEdit(searchTemplate.name);
    setSearchTemplateDescriptionEdit(searchTemplate.description);
    setOriginalSearchTemplateName(searchTemplate.name);
  };

  const handleTemplateCopy = (searchTemplate: SearchTemplate) => {
    setSearchTemplateForCopyAtom(searchTemplate);
    navigate(`/template`);
  };

  const handleTemplateUpdate = (searchTemplate: SearchTemplate) => {
    navigate(`/template/${searchTemplate.id}`);
  };

  return {
    lookupUsers,
    searchesTabSelection,
    setSearchesTabSelection,
    searchName,
    searchType,
    searchDescription,
    searchCreatedBy,
    handleSearchNameChange,
    handleSearchDescriptionChange,
    handleSearchCreatedByChange,
    handleSearchTypeChange,
    handleResetFilter,
    isInitialSearchLoading,
    mySearchesMapped,
    searchPageNoAtomModel,
    searchPageSizeAtomModel,
    setSearchPageNoAtomModel,
    setSearchPageSizeAtomModel,
    searchColumnVisibilityAtomModel,
    setSearchColumnVisibilityAtomModel,
    searchSortAtomModel,
    setSearchSortAtomModel,
    setSearchForDelete,
    setSearchForEdit,
    searchForDelete,
    handleDeleteSearchDialogDelete,
    searchForEdit,
    searchTemplateName,
    searchTemplateDescription,
    searchTemplateCreatedBy,
    handleSearchTemplateNameChange,
    handleSearchTemplateDescriptionChange,
    handleSearchTemplateCreatedByChange,
    handleResetSearchTemplateFilter,
    isInitialSearchTemplateLoading,
    mySearchTemplatesMapped,
    searchTemplatePageNoAtomModel,
    searchTemplatePageSizeAtomModel,
    setSearchTemplatePageNoAtomModel,
    setSearchTemplatePageSizeAtomModel,
    searchTemplateColumnVisibilityAtomModel,
    setSearchTemplateColumnVisibilityAtomModel,
    searchTemplateSortAtomModel,
    setSearchTemplateSortAtomModel,
    setSearchTemplateForDelete,
    setParametersForTemplateEdit,
    handleTemplateUpdate,
    handleTemplateCopy,
    activateCopyLink,
    searchTemplateForDelete,
    handleDeleteSearchTemplateDialogDelete,
    searchTemplateIdEdit,
    setSearchTemplateIdEdit,
    searchTemplateNameEdit,
    setSearchTemplateNameEdit,
    searchTemplateDescriptionEdit,
    setSearchTemplateDescriptionEdit,
    originalSearchTemplateName,
    searchTemplateForLoad,
    handleCloseLoadTemplateModal,
    templateLoadError,
    handleCloseTemplateLoadError,
  };
}
